import MomentUtils from "@date-io/moment";
import Button from "@material-ui/core/Button";
import Divider from "@material-ui/core/Divider";
import FormControl from "@material-ui/core/FormControl";
import FormControlLabel from "@material-ui/core/FormControlLabel";
import FormLabel from "@material-ui/core/FormLabel";
import Grid from "@material-ui/core/Grid";
import Radio from "@material-ui/core/Radio";
import RadioGroup from "@material-ui/core/RadioGroup";
import { makeStyles } from "@material-ui/core/styles";
import TextField from "@material-ui/core/TextField";
import Typography from "@material-ui/core/Typography";
import { DateTimePicker, MuiPickersUtilsProvider } from "@material-ui/pickers";
import { useFormik } from "formik";
import moment from "moment-timezone";
import React, { useEffect } from "react";
import * as yup from "yup";

import * as constants from "../../../../constants";

const VIP = ({ property, disabled, onEdit, onSubmit }) => {
  const formik = useFormik({
    initialValues: {
      cardinal: property.cardinal && {
        ...property.cardinal,
        indefinite: property.cardinal.indefinite ? "true" : "false",
        note: property.cardinal.note ? property.cardinal.note : ""
      }
    },
    validationSchema: yup.object({
      cardinal: yup
        .object({
          note: yup.string(),
          time: yup
            .object({
              start: yup.number(),
              end: yup.number()
            })
            .when("indefinite", (indefinite, schema) =>
              indefinite ? schema.nullable() : schema
            ),
          indefinite: yup.boolean(),
          indefiniteStart: yup
            .number()
            .when("indefinite", (indefinite, schema) =>
              indefinite ? schema : schema.nullable()
            )
        })
        .nullable()
    }),
    onSubmit: async values => {
      onSubmit(
        {
          propertyId: property.propertyId,
          cardinal: values.cardinal && {
            indefinite: values.cardinal.indefinite === "true",
            indefiniteStart:
              values.cardinal.indefinite === "true"
                ? values.cardinal.indefiniteStart
                : undefined,
            time:
              values.cardinal.indefinite !== "true"
                ? values.cardinal.time
                : undefined,
            note: values.cardinal.note ? values.cardinal.note : null
          }
        },
        property => {
          formik.resetForm({
            values: {
              cardinal: property.cardinal && {
                ...property.cardinal,
                indefinite: property.cardinal.indefinite ? "true" : "false",
                note: property.cardinal.note ? property.cardinal.note : ""
              }
            }
          });
        }
      );
    }
  });
  useEffect(() => {
    if (formik.dirty) {
      const resetForm = property => {
        formik.resetForm({
          values: {
            cardinal: property.cardinal && {
              ...property.cardinal,
              indefinite: property.cardinal.indefinite ? "true" : "false",
              note: property.cardinal.note ? property.cardinal.note : ""
            }
          }
        });
      };
      onEdit(formik.handleSubmit, resetForm);
    }
  }, [formik.dirty]);
  useEffect(() => {
    if (!formik.dirty) {
      formik.resetForm({
        values: {
          cardinal: property.cardinal && {
            ...property.cardinal,
            indefinite: property.cardinal.indefinite ? "true" : "false",
            note: property.cardinal.note ? property.cardinal.note : ""
          }
        }
      });
    }
  }, [property]);
  const toggleIndefinite = event => {
    const value = event.target.value;
    if (value === "true") {
      formik.setFieldValue("cardinal.indefinite", value);
      formik.setFieldValue("cardinal.time", null);
      formik.setFieldValue("cardinal.indefiniteStart", Date.now());
    } else {
      const nowMoment = property.place.timezone
        ? moment().tz(property.place.timezone)
        : moment();
      formik.setFieldValue("cardinal.time", {
        start: nowMoment
          .clone()
          .add(1, "days")
          .startOf("day")
          .valueOf(),
        end: nowMoment
          .clone()
          .add(2, "days")
          .startOf("day")
          .valueOf()
      });
      formik.setFieldValue("cardinal.indefinite", value);
    }
  };
  const classes = useStyles();
  if (formik.values && formik.values.cardinal) {
    const timezone = property.place.timezone ? property.place.timezone : "";
    return (
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Typography variant="h5">VIP</Typography>
          <div className={classes.separator} />
          <Divider />
        </Grid>
        <Grid item xs={12}>
          <FormControl component="fieldset" variant="outlined">
            <FormLabel component="legend" className={classes.formControl}>
              Time Frame
            </FormLabel>
            <RadioGroup
              value={formik.values.cardinal.indefinite}
              onChange={toggleIndefinite}
            >
              <FormControlLabel
                value="true"
                control={<Radio disabled={disabled} />}
                label="Indefinite"
              />
              <FormControlLabel
                value="false"
                control={<Radio disabled={disabled} />}
                label="Window"
              />
            </RadioGroup>
          </FormControl>
        </Grid>
        {!(formik.values.cardinal.indefinite === "true") ? (
          <MuiPickersUtilsProvider utils={MomentUtils}>
            <Grid item xs={6}>
              <DateTimePicker
                value={
                  formik.values.cardinal.time && formik.values.cardinal.time.start
                    ? moment(formik.values.cardinal.time.start).tz(timezone)
                    : moment().tz(timezone || moment.tz.guess())
                }
                onChange={m => {
                  if (!m) return;
                  const currentTime = formik.values.cardinal.time || {};
                  formik.setFieldValue("cardinal.time", {
                    ...currentTime,
                    start: m.valueOf()
                  });
                }}
                ampm={false}
                disabled={disabled}
                format={constants.moment.format}
                fullWidth
                inputVariant="filled"
                label="Starts"
              />
            </Grid>
            <Grid item xs={6}>
              <DateTimePicker
                value={
                  formik.values.cardinal.time && formik.values.cardinal.time.end
                    ? moment(formik.values.cardinal.time.end).tz(timezone)
                    : moment().tz(timezone || moment.tz.guess()).add(1, 'day')
                }
                onChange={m => {
                  if (!m) return;
                  const currentTime = formik.values.cardinal.time || {};
                  formik.setFieldValue("cardinal.time", {
                    ...currentTime,
                    end: m.valueOf()
                  });
                }}
                ampm={false}
                disabled={disabled}
                format={constants.moment.format}
                fullWidth
                inputVariant="filled"
                label="Ends"
              />
            </Grid>
          </MuiPickersUtilsProvider>
        ) : (
          <MuiPickersUtilsProvider utils={MomentUtils}>
            <Grid item xs={12}>
              <DateTimePicker
                value={
                  formik.values.cardinal.indefiniteStart
                    ? moment(formik.values.cardinal.indefiniteStart).tz(timezone)
                    : moment().tz(timezone || moment.tz.guess())
                }
                onChange={m => {
                  if (!m) return;
                  formik.setFieldValue(
                    "cardinal.indefiniteStart",
                    m.valueOf()
                  );
                }}
                ampm={false}
                disabled={disabled}
                format={constants.moment.format}
                fullWidth
                inputVariant="filled"
                label="Starts"
              />
            </Grid>
          </MuiPickersUtilsProvider>
        )}
        <Grid item xs={12}>
          <FormControl component="fieldset" variant="outlined" fullWidth>
            <FormLabel component="legend" className={classes.formControl}>
              Notes
            </FormLabel>
            <TextField
              value={formik.values.cardinal.note}
              onChange={formik.handleChange}
              disabled={disabled}
              fullWidth
              label="Note"
              multiline
              name="cardinal.note"
              variant="filled"
            />
          </FormControl>
        </Grid>
        <Grid item xs={12}>
          <Button
            onClick={() => formik.setFieldValue("cardinal", null)}
            color="primary"
            disabled={disabled}
            variant="contained"
          >
            Delete
          </Button>
        </Grid>
      </Grid>
    );
  } else {
    return (
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Typography variant="h5">VIP</Typography>
          <div className={classes.separator} />
          <Divider />
        </Grid>
        <Grid item xs={12}>
          <Typography>No VIP information added.</Typography>
        </Grid>
        <Grid item xs={12}>
          <Button
            onClick={() => {
              const nowMoment = moment();
              formik.setFieldValue("cardinal", {
                note: "",
                indefinite: "true",
                indefiniteStart: nowMoment
                  .clone()
                  .add(1, "hours")
                  .startOf("hour")
                  .valueOf(),
                time: { start: nowMoment.valueOf(), end: nowMoment.valueOf() }
              });
            }}
            color="primary"
            disabled={disabled}
            variant="contained"
          >
            CREATE
          </Button>
        </Grid>
      </Grid>
    );
  }
};

const useStyles = makeStyles(theme => ({
  separator: {
    height: 8
  },
  formControl: {
    marginBottom: theme.spacing(2)
  }
}));

export default VIP;
